import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

// Packages
import { Button, Card, Col, Divider, Flex, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { CheckCircleFilled, CloseCircleFilled, DownloadOutlined, CalendarFilled, SyncOutlined, ToolFilled, UserOutlined, CalendarOutlined, DatabaseOutlined, FileOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Actions
import { confirm_file, discard_file, download_file, download_validation_file, file_check_retry, file_process, get_file_info } from '../../redux/actions/selfActions';
import { get_all_self_settings } from '../../redux/actions/selfSettingsActions';

// Utils
import { get_load_source_desc, get_load_status_desc, get_load_type_desc } from '../../utils/get_status_desc';
import { get_load_status_color } from '../../utils/get_color_status';

// Components
import RandomOutputsModal from './RandomOutputsModal';

const WizardFileInfoView = (props) => {
	const { messageApi, fileId: propFileId, isInitialLoad } = props;

	const { t } = useTranslation();
	const dispatch = useDispatch();
	const { file_id: paramFileId } = useParams();

	// Use prop fileId if provided, otherwise use URL param
	const fileId = propFileId || paramFileId;

	const { isAdminUser } = useSelector(state => state.auth);
	const { file_info_loading, file_info, download_file_loading, download_validation_loading, file_confirm_loading, file_discard_loading } = useSelector(state => state.self);
	const { all_self_settings } = useSelector(state => state.self_settings);

	const fileOrganization = file_info?.organization?._id?.$oid;
	const fileWorkspace = file_info?.workspace?._id?.$oid;
	const weeklyFrequencyEnabled = all_self_settings.settings.some(setting => setting.weekly_frequency === true);

	const [size, setSize] = useState(100);
	const [isRandomModalOpen, setIsRandomModalOpen] = useState(false);

	useEffect(() => {
		const handleResize = () => {
			if (window.innerWidth < 576) {
				setSize('small');
			} else if (window.innerWidth < 768) {
				setSize('middle');
			} else {
				setSize(100);
			}
		};

		handleResize();
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (fileId) {
			dispatch(get_file_info(fileId));
		}
	}, [fileId, dispatch]);

	useEffect(() => {
		if (fileOrganization && fileWorkspace) {
			dispatch(get_all_self_settings({ organization: fileOrganization, workspace: fileWorkspace, skip: 0, limit: 0 }));
		}
	}, [fileOrganization, fileWorkspace, dispatch]);

	const download = () => {
		let fileName = file_info?.description;
		fileName = fileName.replace(/[:\-]/g, '');
		fileName = fileName.replace(/ /g, '_');
		fileName = fileName.replace(/_+/g, '_');
		fileName = `${fileName.toLowerCase()}.csv`;
		dispatch(download_file(messageApi, fileId, fileName));
	}

	const download_validation = () => {
		let fileName = `file_${fileId}_validation.json`;
		dispatch(download_validation_file(messageApi, fileId, fileName));
	}

	const confirm = () => {
		dispatch(confirm_file(messageApi, fileId));
	}

	const discard = () => {
		dispatch(discard_file(messageApi, fileId));
	}

	const InfoItem = ({ icon, label, value, isLast }) => (
		<div style={{ padding: '12px 0', borderBottom: isLast ? 'none' : '1px solid #f0f0f0' }}>
			<Flex align="center" gap={12}>
				{icon}
				<Typography.Text strong style={{ minWidth: '80px', color: '#595959' }}>{label}:</Typography.Text>
				<div>{value}</div>
			</Flex>
		</div>
	);

	return (
		<Flex justify='center'>
			<Card
				bordered={false}
				style={{ width: '100%', maxWidth: 1000 }}
			>
				<Skeleton loading={file_info_loading} active>
					{/* Header */}
					<div style={{ textAlign: 'center', marginBottom: '24px' }}>
						<Typography.Title
							level={3}
							style={{ margin: '0 0 8px 0' }}
							copyable={{
								text: file_info?._id?.$oid,
								tooltips: [t('copyableText.copyId'), t('copyableText.copiedId')]
							}}
						>
							{file_info?.name}
						</Typography.Title>
						<Typography.Text type='secondary' style={{ fontSize: '14px' }}>
							{file_info?.description}
						</Typography.Text>
					</div>

					{/* Cards de información */}
					<Row gutter={[16, 16]}>
						<Col xs={24} md={12}>
							<Card
								size="small"
								title={
									<span>
										<FileOutlined style={{ marginRight: 8 }} />
										{isInitialLoad ? "Información de la Carga" : "Información del Archivo"}
									</span>
								}
								style={{ height: '100%' }}
								headStyle={{ backgroundColor: '#fafafa' }}
							>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#52c41a' }} />}
									label={t('routesTable.headers.status')}
									value={<Tag color={get_load_status_color(file_info?.status)}>{get_load_status_desc(file_info?.status)}</Tag>}
								/>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#722ed1' }} />}
									label={t('loadStatusDetails.card.source')}
									value={get_load_source_desc(file_info?.source)}
								/>
								<InfoItem
									icon={<DatabaseOutlined style={{ color: '#fa8c16' }} />}
									label={t('loadFileInput.loadType')}
									value={get_load_type_desc(file_info?.load_type)}
									isLast={true}
								/>
							</Card>
						</Col>

						<Col xs={24} md={12}>
							<Card
								size="small"
								title={
									<span>
										<UserOutlined style={{ marginRight: 8 }} />
										Información del Usuario
									</span>
								}
								style={{ height: '100%' }}
								headStyle={{ backgroundColor: '#fafafa' }}
							>
								<InfoItem
									icon={<UserOutlined style={{ color: '#1677ff' }} />}
									label={t('routesTable.headers.user')}
									value={file_info?.owner?.username}
								/>
								<InfoItem
									icon={<CalendarOutlined style={{ color: '#faad14' }} />}
									label={t('loadStatus.statusTable.date')}
									value={new Date(file_info?.date?.$date).toLocaleString(localStorage.getItem('i18nextLng'), { hour12: true })}
									isLast={true}
								/>
							</Card>
						</Col>
					</Row>

					{/* Acciones */}
					<Row gutter={[16, 16]} style={{ marginTop: '16px' }}>
						<Col span={24}>
							<Card
								size="small"
								title={
									<span>
										<ToolFilled style={{ marginRight: 8 }} />
										Acciones
									</span>
								}
								style={{ height: '100%' }}
								headStyle={{ backgroundColor: '#fafafa' }}
							>
								<Flex justify='center' align='center' wrap gap={12}>
									{/* Descargas */}
									<Space wrap size={[12, 12]} style={{ justifyContent: 'center' }}>
										<Button
											className="primary-btn"
											icon={<DownloadOutlined />}
											loading={download_file_loading}
											onClick={() => download()}
										>
											{t('loadStatusDetails.download.button')}
										</Button>
										{(isAdminUser && file_info?.validation !== null) &&
											<Button
												variant='solid'
												color='cyan'
												icon={<DownloadOutlined />}
												loading={download_validation_loading}
												onClick={() => download_validation()}
											>
												{t('loadStatusDetails.download.validationBtn')}
											</Button>
										}
										{((file_info?.status === 3 || file_info?.status !== 2 || isAdminUser) && weeklyFrequencyEnabled) &&
											<Button
												variant='solid'
												color='purple'
												icon={<CalendarFilled />}
												onClick={() => setIsRandomModalOpen(true)}
											>
												Aleatorios
											</Button>
										}
									</Space>

									{/* Divider vertical */}
									{(file_info?.status === 3 || file_info?.status !== 2 || isAdminUser) && (
										<Divider type="vertical" style={{ height: '32px', margin: '0 8px' }} />
									)}

									{/* Acciones del archivo */}
									{(file_info?.status === 3 || file_info?.status !== 2 || isAdminUser) && (
										<Space wrap size={[12, 12]} style={{ justifyContent: 'center' }}>
											{file_info?.status === 3 && (
												<>
													<Button
														variant='solid'
														className='success-btn'
														icon={<CheckCircleFilled />}
														loading={file_confirm_loading}
														onClick={() => confirm()}
													>
														{t('loadStatus.statusTable.actionButtons.confirm')}
													</Button>
													<Button
														type='primary'
														danger
														icon={<CloseCircleFilled />}
														loading={file_discard_loading}
														onClick={() => discard()}
													>
														{t('loadStatus.statusTable.actionButtons.discard')}
													</Button>
												</>
											)}
											{(file_info?.status !== 2 || isAdminUser) && (
												<>
													<Button
														variant='solid'
														color='blue'
														icon={<SyncOutlined />}
														onClick={() => dispatch(file_check_retry(messageApi, file_info))}
													>
														{t('loadStatusDetails.check.button')}
													</Button>
													{(file_info?.status === 3 && file_info?.load_type !== 1) &&
														<Button
															variant='solid'
															color='gold'
															icon={<ToolFilled />}
															onClick={() => dispatch(file_process(messageApi, fileId))}
														>
															{t('loadStatusDetails.process.button')}
														</Button>
													}
												</>
											)}
										</Space>
									)}
								</Flex>
							</Card>
						</Col>
					</Row>
				</Skeleton>
			</Card>

			{/* Random Outputs Modal */}
			<RandomOutputsModal
				messageApi={messageApi}
				isModalOpen={isRandomModalOpen}
				setIsModalOpen={setIsRandomModalOpen}
				cycleInfo={file_info?.cycle}
				fileId={fileId}
				orgFilters={{
					organization: file_info?.organization?._id?.$oid,
					workspace: file_info?.workspace?._id?.$oid
				}}
			/>
		</Flex>
	)
}

WizardFileInfoView.propTypes = {
	messageApi: PropTypes.object.isRequired,
	fileId: PropTypes.string,
	isInitialLoad: PropTypes.bool
}

export { WizardFileInfoView as FileInfoCard };

export default WizardFileInfoView;
